import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import '../shared/MessagingActions.css';
import FilterBar from '@company/components/CommunicationHub/FilterBar/FilterBar';
import {
  getJobChats,
  markAllJobChatsAsRead,
} from '@company/services/com-hub/api';
import ThPagination from '@components/ThPagination/ThPagination';
import ComHubSkeletonLoader from '@company/components/CommunicationHub/ComHubSkeletonLoader';
import {
  JobChat,
  ChatModeEnum,
  ComHubQueryParamsEnum,
  WorkerModalParams,
} from '@company/types/comm-hub.type';
import QueryKeys from '@constants/queryKeys';
import {
  handleComHubSearch,
  handleBatchMarkJobChatsAsRead,
  extractComHubUrlParams,
} from '@utils/ComHubUtils';
import JobMessagesTable from '@company/components/CommunicationHub/CommunicationHubTables/JobMessagesTable';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';
import { useChatContext } from '@company/state/chatContext';
import { BUTTON_COOLDOWN_DURATION_MS } from '@constants/various';

interface JobChatsProps {
  onShowWorkerProfile?: (data: WorkerModalParams) => void;
  onShowAlert?: (success: boolean, message: string) => void;
  onCounterUpdate: (delta: number) => void;
  onResetCounter?: () => void;
}

function JobMessages({
  onShowWorkerProfile,
  onShowAlert,
  onCounterUpdate,
  onResetCounter,
}: JobChatsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchState, setSearchState] = useState<{
    searchText: string;
    dateRange: { startDate: Date | undefined; endDate: Date | undefined };
  }>();
  const [showOnlyNew, setShowOnlyNew] = useState(false);
  const [chatMode, setChatMode] = useState<ChatModeEnum | null>(
    ChatModeEnum.INBOX
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCooldown, setIsCooldown] = useState(false);
  const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();
  const { batchMarkAsRead, markAsRead, markAsUnread } = useChatContext();

  // CONSTANTS FOR QUERY
  const queryKeyJobChats = [
    QueryKeys.ComHub.JOB_CHAT,
    currentOffset,
    showOnlyNew,
    chatMode,
    searchState,
  ];

  // useQuery fetch
  const {
    data: jobChatsResponse,
    isLoading,
    error: jobChatsError,
  } = useQuery({
    queryKey: queryKeyJobChats,
    queryFn: () =>
      getJobChats(
        currentOffset,
        DEFAULT_ITEMS_PER_PAGE,
        searchState?.searchText,
        searchState?.dateRange,
        showOnlyNew,
        chatMode
      ),
    enabled: isInitialized, // Only run query after initialization
  });

  // Handle query errors
  useEffect(() => {
    if (jobChatsError) {
      onShowAlert?.(false, jobChatsError.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobChatsError]);

  // Initialize state from URL parameters
  useEffect(() => {
    if (isInitialized) return;

    const {
      searchState: urlSearchState,
      showOnlyNew: urlShowOnlyNew,
      offset: urlOffset,
    } = extractComHubUrlParams(searchParams);

    if (urlSearchState) {
      setSearchState(urlSearchState);
    }
    setShowOnlyNew(!!urlShowOnlyNew);
    setCurrentOffset(Number(urlOffset) || 0);
    setIsInitialized(true);
  }, [searchParams, isInitialized]);

  useEffect(() => {
    return () => {
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
      }
    };
  }, []);

  const { mutate: markAllJobChats, isPending: isMarkingAll } = useMutation({
    mutationFn: async () => {
      const response = await markAllJobChatsAsRead();

      if (!response?.success) {
        throw new Error(
          response?.message ||
            response?.error ||
            'Failed to mark messages as read.'
        );
      }

      return response;
    },
    onSuccess: async (response) => {
      onShowAlert?.(
        true,
        response.message || 'All job messages marked as read.'
      );

      // Invalidate list query and reset counter to 0
      await queryClient.invalidateQueries({
        queryKey: queryKeyJobChats,
      });
      // Mark all as read resets counter to 0
      onResetCounter?.();
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to mark messages as read.';
      onShowAlert?.(false, message);
    },
    onSettled: () => {
      setIsCooldown(true);
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
      }
      cooldownTimeoutRef.current = setTimeout(() => {
        setIsCooldown(false);
      }, BUTTON_COOLDOWN_DURATION_MS);
    },
  });

  const handleMarkAllAsRead = useCallback(() => {
    if (isMarkingAll || isCooldown) {
      return;
    }

    markAllJobChats();
  }, [isMarkingAll, isCooldown, markAllJobChats]);
  const unreadJobChats = useMemo<JobChat[]>(() => {
    if (!jobChatsResponse?.jobChats) {
      return [];
    }

    return jobChatsResponse.jobChats.filter(
      (jobChat) => jobChat.unreadMessages > 0
    );
  }, [jobChatsResponse]);

  const unreadJobChatsCountResponse = unreadJobChats.length;

  const readJobChats = useMemo<JobChat[]>(() => {
    if (!jobChatsResponse?.jobChats) {
      return [];
    }

    return jobChatsResponse.jobChats.filter(
      (jobChat) => jobChat.unreadMessages === 0 && jobChat.hasMultipleAuthors
    );
  }, [jobChatsResponse]);

  const handleMarkAllAsUnread = useCallback(() => {
    if (isCooldown || isMarkingAll || !readJobChats.length) {
      return;
    }

    readJobChats.forEach((jobChat) => {
      markAsUnread(jobChat.chatId, () => onCounterUpdate(1));
    });
  }, [isCooldown, isMarkingAll, markAsUnread, readJobChats, onCounterUpdate]);

  const handleMarkUnreadChats = useCallback(() => {
    if (isCooldown || isMarkingAll || !unreadJobChats.length) {
      return;
    }

    const count = unreadJobChats.length;
    handleBatchMarkJobChatsAsRead(
      unreadJobChats,
      batchMarkAsRead,
      markAsRead,
      markAsUnread,
      (message) => message.chatId,
      (message) => message.unreadMessages > 0,
      // Only update counter on successful API call
      () => onCounterUpdate(-count)
    );
  }, [
    batchMarkAsRead,
    isCooldown,
    isMarkingAll,
    markAsRead,
    markAsUnread,
    unreadJobChats,
    onCounterUpdate,
  ]);

  const updateSearchParams = useCallback(
    (params: URLSearchParams) => {
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handleOnSearch = useCallback(
    (search: {
      searchText: string;
      dateRange: { startDate: Date | undefined; endDate: Date | undefined };
    }) => {
      handleComHubSearch(search, setSearchState, updateSearchParams);
      setCurrentOffset(0);
    },
    [updateSearchParams]
  );

  // Update URL when offset changes
  useEffect(() => {
    if (!isInitialized) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(ComHubQueryParamsEnum.OFFSET, currentOffset.toString());
    newSearchParams.set(
      ComHubQueryParamsEnum.MAX,
      DEFAULT_ITEMS_PER_PAGE.toString()
    );
    updateSearchParams(newSearchParams);
  }, [currentOffset, searchParams, updateSearchParams, isInitialized]);

  return (
    <>
      <FilterBar
        onSearch={handleOnSearch}
        showOnlyNew={showOnlyNew}
        onShowOnlyNew={() => {
          setShowOnlyNew((prevState) => !prevState);
          setCurrentOffset(0);
        }}
        isMessagingTab
        chatMode={chatMode}
        onChatModeChange={(mode) => {
          setChatMode(mode);
          setCurrentOffset(0);
        }}
        onMarkAllAsRead={handleMarkAllAsRead}
        onMarkAllAsUnread={
          readJobChats.length ? handleMarkAllAsUnread : undefined
        }
        markAllAsUnreadLabel={`Mark ${readJobChats.length} unread`}
        onMarkUnreadMessages={handleMarkUnreadChats}
        markUnreadMessagesLabel={`Mark ${unreadJobChatsCountResponse} read`}
      />
      {isLoading && <ComHubSkeletonLoader />}
      {jobChatsResponse ? (
        <>
          {jobChatsResponse.jobChats ? (
            <JobMessagesTable
              jobChats={jobChatsResponse.jobChats}
              onShowWorkerProfile={onShowWorkerProfile}
              showHeader={false}
              onCounterUpdate={onCounterUpdate}
            />
          ) : (
            <p>No new notifications.</p>
          )}

          <ThPagination
            currentOffset={jobChatsResponse.pagination?.offset}
            onPageChange={setCurrentOffset}
            itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
            totalItems={jobChatsResponse.pagination?.count}
          />
        </>
      ) : (
        <p className="mt-5 d-flex justify-content-center">
          {!isLoading && 'There are no job messages.'}
        </p>
      )}
    </>
  );
}

export default JobMessages;
